import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { ApiError } from '@shared/api/errors/errors';
import { formatAuditDateTime } from '@entities/audit-log';
import { useAuditLogExport } from '@features/audit-log';
import type { AuditLogFilters } from '@entities/audit-log';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@shared/ui';

interface AuditExportButtonProps {
  filters: AuditLogFilters;
  period: { from: string; to: string } | null;
  disabled: boolean;
  onExported: () => void;
}

export function AuditExportButton({ filters, period, disabled, onExported }: AuditExportButtonProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { exportCsv, isExporting } = useAuditLogExport(filters);
  const [open, setOpen] = useState(false);
  const from = period?.from ?? filters.dateFrom;
  const to = period?.to ?? filters.dateTo;

  const confirm = async () => {
    try {
      const result = await exportCsv();
      toast(t('auditLog.export.done', { count: result.rows }), 'success');
      if (result.truncated) toast(t('auditLog.export.truncated'), 'info');
      onExported();
      setOpen(false);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : t('auditLog.errors.export');
      toast(message, 'error');
    }
  };

  return (
    <>
      <Button type="button" variant="outline" disabled={disabled || isExporting} onClick={() => setOpen(true)}>
        <Download className="h-4 w-4" />
        {t('auditLog.export.button')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auditLog.export.button')}</DialogTitle>
            <DialogDescription>
              {t('auditLog.export.confirm', {
                from: from ? formatAuditDateTime(from) : t('auditLog.export.defaultPeriod'),
                to: to ? formatAuditDateTime(to) : t('auditLog.export.defaultPeriod'),
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('auditLog.export.cancel')}
            </Button>
            <Button type="button" onClick={() => void confirm()} disabled={isExporting}>
              {t('auditLog.export.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
